import { io, type Socket } from "socket.io-client";

export interface RealtimeEvent {
  type: string;
  channel: string;
  payload: unknown;
  timestamp: string;
}

interface RealtimeConfig {
  url: string;
  token?: string;
  onEvent?: (event: RealtimeEvent) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
}

type ChannelHandler = (event: RealtimeEvent) => void;

export class RealtimeClient {
  public url: string;
  public token: string | undefined;
  public onEvent: ((event: RealtimeEvent) => void) | undefined;
  public onConnected: (() => void) | undefined;
  public onDisconnected: (() => void) | undefined;
  public onError: ((error: Error) => void) | undefined;
  private socket: Socket | null = null;
  private channels: Map<string, Set<ChannelHandler>> = new Map();
  public isConnected = false;

  constructor(config: RealtimeConfig) {
    this.url = config.url;
    this.token = config.token;
    this.onEvent = config.onEvent;
    this.onConnected = config.onConnected;
    this.onDisconnected = config.onDisconnected;
    this.onError = config.onError;
  }

  connect(): void {
    if (this.socket?.connected) return;

    try {
      this.socket = io(this.url, {
        auth: this.token ? { token: this.token } : undefined,
        transports: ["websocket"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        reconnectionAttempts: 10,
      });

      this.socket.on("connect", () => {
        this.isConnected = true;
        this.onConnected?.();
      });

      this.socket.on("disconnect", () => {
        this.isConnected = false;
        this.onDisconnected?.();
      });

      this.socket.on("connect_error", (error) => {
        this.onError?.(error as Error);
      });

      this.socket.onAny((event, data) => {
        const realtimeEvent: RealtimeEvent = {
          type: event,
          channel: data?.channel ?? "",
          payload: data,
          timestamp: new Date().toISOString(),
        };
        this.onEvent?.(realtimeEvent);
        this.handleChannelEvent(realtimeEvent);
      });
    } catch (error) {
      this.onError?.(error as Error);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
  }

  subscribe(channel: string, handler: ChannelHandler): () => void {
    if (!this.channels.has(channel)) {
      this.channels.set(channel, new Set());
    }
    this.channels.get(channel)!.add(handler);

    if (this.socket?.connected) {
      this.socket.emit("subscribe", { channel });
    }

    return () => {
      this.channels.get(channel)?.delete(handler);
      if (this.channels.get(channel)?.size === 0) {
        this.channels.delete(channel);
        if (this.socket?.connected) {
          this.socket.emit("unsubscribe", { channel });
        }
      }
    };
  }

  publish(channel: string, event: RealtimeEvent): void {
    if (this.socket?.connected) {
      this.socket.emit(event.type, { ...(event.payload as Record<string, unknown>), channel });
    }
  }

  private handleChannelEvent(event: RealtimeEvent): void {
    const handlers = this.channels.get(event.channel);
    if (handlers) {
      for (const handler of handlers) {
        handler(event);
      }
    }
  }

  getOnlineUserIds(): string[] {
    return [];
  }

  isUserOnline(_userId: string): boolean {
    return false;
  }
}

export const realtimeClient = new RealtimeClient({
  url: process.env.NEXT_PUBLIC_WS_URL || "http://localhost:4100",
});